import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { throwIfNotOk } from "@/lib/api-error";
import { MagnifyingGlass, Plus } from "@phosphor-icons/react";
import { ServiceCard } from "@/components/admin/services/ServiceCard";
import { ServiceFormDialog } from "@/components/admin/services/ServiceFormDialog";
import { ServiceDeleteDialog } from "@/components/admin/services/ServiceDeleteDialog";
import { ServiceEmptyState } from "@/components/admin/services/ServiceEmptyState";
import { ServiceSkeletonCard } from "@/components/admin/services/ServiceSkeletonCard";
import type { Service } from "@/types";

export function ServicesCatalogSection() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const editServiceId = editingService?.id ?? null;
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data: servicesEnv, isLoading } = useQuery({
    queryKey: ["admin", "services", "platform", minPrice, maxPrice],
    queryFn: async () => {
      const params = new URLSearchParams({ all: "1", createdByRole: "admin" });
      if (minPrice.trim()) params.set("minPrice", minPrice.trim());
      if (maxPrice.trim()) params.set("maxPrice", maxPrice.trim());

      const res = await fetch(`/api/services?${params}`);
      if (!res.ok) throw new Error("Failed to fetch services");
      return res.json() as Promise<{ services: Service[]; resolvedCountry: string | null }>;
    },
  });
  const services = servicesEnv?.services ?? [];

  const { data: editService } = useQuery({
    queryKey: ["services", "detail", editServiceId],
    queryFn: async () => {
      const res = await fetch(`/api/services/${editServiceId}`);
      if (!res.ok) throw new Error("Failed to load service");
      return res.json() as Promise<Service>;
    },
    enabled: !!editServiceId,
  });

  const createMutation = useMutation({
    mutationFn: async (data: Partial<Service>) => {
      const res = await fetch("/api/services", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      await throwIfNotOk(res, "Greška pri kreiranju usluge");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "services"] });
      setFormOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: Partial<Service> & { id: string }) => {
      const res = await fetch(`/api/services/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      await throwIfNotOk(res, "Greška pri ažuriranju usluge");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "services"] });
      setEditingService(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/services/${id}`, { method: "DELETE" });
      await throwIfNotOk(res, "Greška pri brisanju usluge");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "services"] });
      setDeletingId(null);
    },
  });

  const filtered = services.filter(
    (s) =>
      s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.description.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="grid w-full gap-3 md:max-w-2xl md:grid-cols-[minmax(220px,1fr)_120px_120px]">
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Pretraži usluge..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Input
            type="number"
            min="0"
            inputMode="decimal"
            placeholder="Cijena od"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
          />
          <Input
            type="number"
            min="0"
            inputMode="decimal"
            placeholder="Cijena do"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
          />
        </div>
        <Button onClick={() => setFormOpen(true)}>
          <Plus className="size-4" />
          Nova usluga
        </Button>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <ServiceSkeletonCard key={i} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <ServiceEmptyState
          hasSearch={
            searchQuery.length > 0 || minPrice.length > 0 || maxPrice.length > 0
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
              onEdit={() => {
                setEditingService(service);
              }}
              onDelete={() => setDeletingId(service.id)}
            />
          ))}
        </div>
      )}

      {formOpen && (
        <ServiceFormDialog
          service={null}
          isPending={createMutation.isPending}
          onSubmit={(data) => createMutation.mutate(data)}
          onClose={() => setFormOpen(false)}
        />
      )}

      {editingService && (
        <ServiceFormDialog
          service={editService ?? editingService}
          isPending={updateMutation.isPending}
          onSubmit={(data) => updateMutation.mutate({ id: editingService.id, ...data })}
          onClose={() => {
            setEditingService(null);
          }}
        />
      )}

      {deletingId && (
        <ServiceDeleteDialog
          isPending={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate(deletingId)}
          onClose={() => setDeletingId(null)}
        />
      )}
    </div>
  );
}
